import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';

const GalleryPage = () => {
  // Don't destructure useAuth if we're not using any of its properties
  useAuth(); // Just call the hook without destructuring
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [pitchDecks, setPitchDecks] = useState([]);
  const [filter, setFilter] = useState('all'); // all, funding, partnering, services, license
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest'); // newest, most_viewed, most_liked
  
  // Create refs to track focus and previous location
  const prevLocationRef = useRef(location.pathname);
  const refreshTimeoutRef = useRef(null);

  // Use useCallback to memoize the fetchGalleryPitches function
  const fetchGalleryPitches = useCallback(async () => {
    setLoading(true);
    
    try {
      // Build the base query
      let query = supabase
        .from('gallery_pitches')
        .select(`
          id, 
          title, 
          description, 
          objective, 
          preview_image_url, 
          created_at, 
          view_count,
          product_id,
          user_id,
          products (
            name
          ),
          gallery_likes (
            id
          )
        `)
        .eq('is_published', true);
      
      // Apply filters
      if (filter !== 'all') {
        query = query.eq('objective', filter);
      }
      
      // Apply sorting
      let queryWithOrder;
      if (sortBy === 'newest') {
        queryWithOrder = query.order('created_at', { ascending: false });
      } else if (sortBy === 'most_viewed') {
        queryWithOrder = query.order('view_count', { ascending: false });
      } else {
        // Default order or no specific order needed
        queryWithOrder = query;
      }
      
      // Execute the query
      const { data, error } = await queryWithOrder;
      
      if (error) {
        throw error;
      }
      
      // Fetch user profiles separately if we have data
      if (data && data.length > 0) {
        // Get unique user IDs
        const userIds = [...new Set(data.map(pitch => pitch.user_id))];
        
        // Fetch user profiles
        const { data: userProfiles, error: userProfilesError } = await supabase
          .from('user_profiles')
          .select('user_id, name')
          .in('user_id', userIds);
        
        // Add user profile info to each pitch
        if (!userProfilesError && userProfiles) {
          data.forEach(pitch => {
            const profile = userProfiles.find(p => p.user_id === pitch.user_id);
            pitch.user_profile = profile || { name: 'Unknown User' };
            
            // Replace missing names with "Anonymous" instead of showing email
            if (!pitch.user_profile.name || pitch.user_profile.name.includes('@')) {
              pitch.user_profile.name = 'Anonymous';
            }
          });
        }
      }
      
      // For most_liked sorting
      if (sortBy === 'most_liked') {
        data.sort((a, b) => {
          return (b.gallery_likes?.length || 0) - (a.gallery_likes?.length || 0);
        });
      }
      
      // Apply search if defined
      let filteredData = data;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredData = data.filter(pitch => 
          pitch.title.toLowerCase().includes(query) || 
          pitch.description.toLowerCase().includes(query) ||
          pitch.products.name.toLowerCase().includes(query)
        );
      }
      
      setPitchDecks(filteredData);
    } catch (error) {
      console.error('Error fetching gallery pitches:', error);
    } finally {
      setLoading(false);
    }
  }, [filter, sortBy, searchQuery]); // Add dependencies here

  // Effect to load data initially
  useEffect(() => {
    fetchGalleryPitches();
  }, [fetchGalleryPitches]); // Now correctly depends on the memoized function
  
  // Effect to refresh data when returning to the gallery page
  useEffect(() => {
    // If we've navigated back to the gallery from a pitch detail page, refresh data
    if (
      location.pathname === '/gallery' && 
      prevLocationRef.current !== '/gallery' && 
      prevLocationRef.current.includes('/gallery/pitch/')
    ) {
      // Clear any existing timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      
      // Slight delay to ensure navigation is complete
      refreshTimeoutRef.current = setTimeout(() => {
        fetchGalleryPitches();
      }, 300);
    }
    
    // Update the previous location ref
    prevLocationRef.current = location.pathname;
    
    // Cleanup timeout on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [location.pathname, fetchGalleryPitches]);
  
  // Add visibility change listener to refresh data when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && location.pathname === '/gallery') {
        fetchGalleryPitches();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchGalleryPitches, location.pathname]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchGalleryPitches();
  };

  if (loading) {
    return <LoadingScreen message="Loading Gallery..." />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row justify-between items-center mb-8">
        <h1 className="text-3xl font-bold mb-4 md:mb-0">Pitch Gallery</h1>
        
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 w-full md:w-auto">
          <form onSubmit={handleSearch} className="flex">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search pitches..."
              className="px-4 py-2 border rounded-l focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button 
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded-r hover:bg-blue-700"
            >
              Search
            </button>
          </form>
          
          <button
            onClick={() => fetchGalleryPitches()}
            className="flex items-center justify-center px-4 py-2 border rounded bg-gray-100 hover:bg-gray-200"
            title="Refresh the gallery data"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>
      
      <div className="mb-8 flex flex-wrap gap-4">
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
          <div>
            <label htmlFor="filter" className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Objective
            </label>
            <select
              id="filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Objectives</option>
              <option value="Funding">Funding</option>
              <option value="Partnering">Partnering</option>
              <option value="Request Services">Request Services</option>
              <option value="License Out">License Out</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="sortBy" className="block text-sm font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <select
              id="sortBy"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="newest">Newest First</option>
              <option value="most_viewed">Most Viewed</option>
              <option value="most_liked">Most Liked</option>
            </select>
          </div>
        </div>
      </div>
      
      {pitchDecks.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <h3 className="text-xl font-medium text-gray-700">No pitch decks found</h3>
          <p className="text-gray-500 mt-2">Try changing your search criteria or check back later.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pitchDecks.map((pitch) => (
            <Link 
              to={`/gallery/pitch/${pitch.id}`} 
              key={pitch.id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden flex flex-col"
            >
              <div className="h-48 bg-gray-100 relative">
                {pitch.preview_image_url ? (
                  <img 
                    src={pitch.preview_image_url} 
                    alt={pitch.title} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-200">
                    <span className="text-gray-400">No preview available</span>
                  </div>
                )}
                <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs font-bold px-2.5 py-0.5 rounded">
                  {pitch.objective}
                </div>
              </div>
              
              <div className="p-4 flex-grow">
                <h3 className="text-lg font-semibold mb-2 line-clamp-2">{pitch.title}</h3>
                <p className="text-gray-600 text-sm mb-3 line-clamp-3">{pitch.description}</p>
                
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <span>By: {pitch.user_profile.name}</span>
                  <span>{new Date(pitch.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              
              <div className="px-4 py-3 bg-gray-50 border-t flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-blue-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                    </svg>
                    <span className="text-sm">{pitch.gallery_likes?.length || 0}</span>
                  </div>
                  
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-blue-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">{pitch.view_count || 0} Views</span>
                  </div>
                </div>
                
                <span className="text-sm text-blue-600 font-medium">View Details</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default GalleryPage; 